import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar, Icon } from '../../tcn/ui-kit';
import { TCN, TCN_COLOR_MAP } from '../../tcn/ui-kit/tokens';
import { formatInt, formatDiamonds, isNum } from '../shared/format';

/**
 * Module 1 — Overview Metric Card (mockup A1.2 — module1-performance.jsx:124-170).
 *
 * Renders ONE Overview metric (Diamonds OR Recruited Creators) as a 2×2 grid of
 * the four volume dims + a target-completion bar + a growth footer:
 *   - Monthly target / MTD target / MTD actual / Last month
 *   - Target completion (% vs monthly target) with progress bar
 *   - Growth target vs LM
 *
 * Diamonds are shown compact (4.12M) WITHOUT USD, per mockup. The MTD-pace
 * completion is surfaced separately by PaceFlagsCard on the same page.
 *
 * Props:
 *   - title: section heading
 *   - kind: 'diamonds' | 'recruited' — drives which fields are read from `data`.
 *   - data: manager_current_month row.
 *   - accent: semantic color key ('diamond' | 'cyan') — icon + MTD-actual color.
 *   - isPriority: when true (manager marked this metric as today's top priority
 *     via PaceFlagsCard), show a small "★ PRIORITY" pill next to the title so
 *     the commitment is visible across the Performance module, not only on the
 *     Pace card. State lives in TcnMeIndex; toggling Pace re-renders here.
 */
const OverviewMetricCard = ({ title, kind, data, accent = 'diamond', isPriority = false }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const isDiamonds = kind === 'diamonds';
  const accentColor = TCN_COLOR_MAP[accent] ?? TCN.text;

  // Phase A: manager_current_month exposes `*_clear` mirror columns (R2-safe).
  const monthlyTarget = isDiamonds ? data.diamonds_monthly_target_clear : data.recruited_creators_target;
  const mtdTarget = isDiamonds ? data.diamonds_mtd_target_clear : data.recruited_creators_mtd_target;
  const mtdActual = isDiamonds ? data.diamonds_mtd_actual_clear : data.recruited_creators_mtd_actual;
  const lastMonth = isDiamonds ? data.diamonds_last_month_clear : data.recruited_creators_last_month;
  const completion = isDiamonds
    ? data.diamonds_completion_pct_vs_monthly
    : data.recruited_creators_completion_pct_vs_monthly;
  const growthTargetPct = isDiamonds
    ? data.diamonds_growth_target_pct
    : data.recruited_creators_growth_target_pct;

  const fmt = (v) => (isDiamonds ? formatDiamonds(v) : formatInt(v));

  const cells = [
    { label: t('tcn.m1.overview.monthlyTarget', 'Monthly target'), value: fmt(monthlyTarget), color: TCN.text },
    { label: t('tcn.m1.overview.mtdTarget', 'MTD target'), value: fmt(mtdTarget), color: TCN.textDim },
    { label: t('tcn.m1.overview.mtdActual', 'MTD actual'), value: fmt(mtdActual), color: accentColor },
    { label: t('tcn.m1.overview.lastMonth', 'Last month'), value: fmt(lastMonth), color: TCN.textDim },
  ];

  const hasCompletion = isNum(completion);
  const hasGrowth = isNum(growthTargetPct);
  const growthRounded = hasGrowth ? Math.round(growthTargetPct * 10) / 10 : null;

  return (
    <Card accent={accent}>
      {/* Header — icon + metric name (+ "★ PRIORITY" pill if manager flagged it
          today via PaceFlagsCard). Pill stays compact so the row holds at 375px. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Icon name={isDiamonds ? 'diamond' : 'people'} size={18} color={accentColor} />
        <span style={{ fontSize: 14, fontWeight: 700, color: TCN.text }}>{title}</span>
        {isPriority && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: TCN.amber,
              border: `1px solid ${TCN.amber}66`,
              background: `${TCN.amber}14`,
              borderRadius: 6,
              padding: '2px 6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Icon name="flame" size={10} color={TCN.amber} />
            {t('tcn.m1.overview.priorityPill', "Today's priority")}
          </span>
        )}
      </div>

      {/* 2×2 grid of volume dims */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {cells.map((cell, i) => (
          <div key={i}>
            <div
              style={{
                fontSize: 10,
                color: TCN.textMute,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {cell.label}
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginTop: 2,
                color: cell.color,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {cell.value}
            </div>
          </div>
        ))}
      </div>

      {/* Target completion */}
      {hasCompletion && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: TCN.textDim, fontWeight: 600 }}>
              {t('tcn.m1.overview.targetCompletion', 'Target completion')}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: completion >= 100 ? TCN.green : completion >= 80 ? TCN.amber : TCN.red,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Math.round(completion)}%
            </span>
          </div>
          <ProgressBar value={completion} target={100} animated />
        </div>
      )}

      {/* Growth footer */}
      {hasGrowth && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${TCN.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: TCN.textDim,
          }}
        >
          <span>{t('tcn.m1.overview.growthTarget', 'Growth target')}</span>
          <span style={{ color: TCN.text, fontWeight: 700 }}>
            {growthRounded >= 0 ? '+' : ''}
            {growthRounded}
            {t('tcn.m1.overview.vsLm', '% vs LM')}
          </span>
        </div>
      )}
    </Card>
  );
};

export default OverviewMetricCard;
